import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import {
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
  StatusOK,
  StatusError,
  StatusRunning,
  StatusPending,
} from '@backstage/core-components';
import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { argoWorkflowsApiRef } from '../../api/ArgoWorkflowsApi';
import { WorkflowPhase, WorkflowSummary } from '../../api/types';
import { PHASE_COLOR } from '../../constants';
import { SubmitWorkflowDialog } from './SubmitWorkflowDialog';

interface WorkflowListProps {
  namespace?: string;
  labelSelector?: string;
}

const ALL_PHASES: WorkflowPhase[] = ['Succeeded', 'Failed', 'Error', 'Running', 'Pending'];

const phaseStatus = (phase: WorkflowSummary['phase']) => {
  switch (phase) {
    case 'Succeeded':
      return <StatusOK>Succeeded</StatusOK>;
    case 'Failed':
    case 'Error':
      return <StatusError>{phase}</StatusError>;
    case 'Running':
      return <StatusRunning>Running</StatusRunning>;
    default:
      return <StatusPending>{phase}</StatusPending>;
  }
};

const formatDuration = (start?: string, finish?: string): string => {
  if (!start) return '';
  const startMs = Date.parse(start);
  const endMs = finish ? Date.parse(finish) : Date.now();
  const seconds = Math.max(0, Math.round((endMs - startMs) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

export function WorkflowList({ namespace: propNamespace, labelSelector }: WorkflowListProps) {
  const api = useApi(argoWorkflowsApiRef);
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [namespace, setNamespace] = useState(propNamespace || 'ci');
  const [selectedPhases, setSelectedPhases] = useState<WorkflowPhase[]>([]);
  const [templateFilter, setTemplateFilter] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [grouped, setGrouped] = useState(false);
  const [discoveredNs, setDiscoveredNs] = useState<string[]>([]);

  useEffect(() => {
    api.discoverNamespaces().then(ns => {
      setDiscoveredNs(ns);
      if (!propNamespace && ns.length > 0 && !ns.includes(namespace)) {
        setNamespace(ns.includes('ci') ? 'ci' : ns[0]);
      }
    });
  }, [api, propNamespace, namespace]);

  const state = useAsync(
    () =>
      api.listWorkflows({
        namespace,
        labelSelector,
        phases: selectedPhases.length > 0 ? selectedPhases : undefined,
        limit: 50,
      }),
    [namespace, labelSelector, selectedPhases, refreshKey],
  );

  useEffect(() => {
    if (state.error) return undefined;
    const id = setInterval(() => setRefreshKey(k => k + 1), 15000);
    return () => clearInterval(id);
  }, [state.error]);

  if (state.loading && !state.value) return <Progress />;
  if (state.error) return <ResponseErrorPanel error={state.error} />;

  const items = state.value ?? [];
  const templates = [...new Set(items.map(i => i.templateName).filter(Boolean))] as string[];
  const filtered = templateFilter
    ? items.filter(i => i.templateName === templateFilter)
    : items;

  const columns: TableColumn<WorkflowSummary>[] = [
    {
      title: 'Status',
      field: 'phase',
      render: row => phaseStatus(row.phase),
      width: '120px',
    },
    {
      title: 'Name',
      field: 'name',
      render: row => (
        <a
          href={`/argo-workflows/${row.namespace}/${row.name}`}
          onClick={e => {
            e.preventDefault();
            navigate(`/argo-workflows/${row.namespace}/${row.name}`);
          }}
        >
          {row.name}
        </a>
      ),
    },
    { title: 'Template', field: 'templateName' },
    { title: 'Started', field: 'startedAt' },
    {
      title: 'Duration',
      render: row => formatDuration(row.startedAt, row.finishedAt),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 0',
          flexWrap: 'wrap',
        }}
      >
        {!propNamespace && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
            <InputLabel>Namespace</InputLabel>
            <Select
              value={namespace}
              onChange={e => setNamespace(e.target.value as string)}
              label="Namespace"
            >
              {discoveredNs.map(ns => (
                <MenuItem key={ns} value={ns}>{ns}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {ALL_PHASES.map(phase => {
            const active = selectedPhases.includes(phase);
            return (
              <Chip
                key={phase}
                label={phase}
                size="small"
                clickable
                onClick={() => {
                  setSelectedPhases(prev =>
                    active ? prev.filter(p => p !== phase) : [...prev, phase],
                  );
                }}
                style={{
                  backgroundColor: active ? PHASE_COLOR[phase] : undefined,
                  color: active ? (phase === 'Running' ? '#1a2332' : '#fff') : undefined,
                  borderColor: PHASE_COLOR[phase],
                }}
                variant={active ? 'default' : 'outlined'}
              />
            );
          })}
        </div>

        <Autocomplete
          size="small"
          options={templates}
          value={templateFilter}
          onChange={(_, v) => setTemplateFilter(v)}
          renderInput={params => (
            <TextField {...params} label="Template" variant="outlined" style={{ minWidth: 200 }} />
          )}
          style={{ minWidth: 200 }}
        />
        <div style={{ flex: 1 }} />
        <Button
          size="small"
          variant={grouped ? 'contained' : 'outlined'}
          onClick={() => setGrouped(g => !g)}
          startIcon={grouped ? <ViewModuleIcon /> : <ViewListIcon />}
          style={{ marginRight: 8 }}
        >
          {grouped ? 'Grouped' : 'Flat'}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setSubmitOpen(true)}
        >
          Submit
        </Button>
      </div>
      <SubmitWorkflowDialog
        namespace={namespace}
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmitted={(ns, wfName) => {
          setSubmitOpen(false);
          navigate(`/argo-workflows/${ns}/${wfName}`);
        }}
      />

      {filtered.length === 0 ? (
        <div style={{ padding: 16 }}>
          No workflows match the current filters in <code>{namespace}</code>.
        </div>
      ) : grouped ? (
        <GroupedView items={filtered} navigate={navigate} />
      ) : (
        <Table
          title={`Workflows (${filtered.length})`}
          options={{ paging: false, search: true }}
          columns={columns}
          data={filtered}
        />
      )}
    </div>
  );
}

function GroupedView({
  items,
  navigate,
}: {
  items: WorkflowSummary[];
  navigate: ReturnType<typeof useNavigate>;
}) {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const groups = items.reduce<Record<string, WorkflowSummary[]>>((acc, wf) => {
    const key = wf.templateName || '(no template)';
    (acc[key] ??= []).push(wf);
    return acc;
  }, {});

  const sorted = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));

  const toggle = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div>
      {sorted.map(([tmpl, wfs]) => {
        const isCollapsed = collapsed.has(tmpl);
        const latest = wfs[0];
        const latestColor = PHASE_COLOR[latest.phase] ?? '#9e9e9e';
        return (
          <div key={tmpl} style={{ marginBottom: 4 }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggle(tmpl)}
              onKeyDown={e => { if (e.key === 'Enter') toggle(tmpl); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                borderRadius: 4,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{ fontSize: 10, width: 14 }}>{isCollapsed ? '▶' : '▼'}</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{tmpl}</span>
              <Chip size="small" label={`${wfs.length}`} style={{ height: 18, fontSize: 11 }} />
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: latestColor,
                }}
              />
              <span style={{ fontSize: 12, color: theme.palette.text.secondary }}>
                latest: {latest.phase}
              </span>
            </div>
            {!isCollapsed && (
              <div style={{ paddingLeft: 24 }}>
                {wfs.map(wf => (
                  <div
                    key={wf.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '4px 8px',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      fontSize: 13,
                    }}
                  >
                    {phaseStatus(wf.phase)}
                    <a
                      href={`/argo-workflows/${wf.namespace}/${wf.name}`}
                      onClick={e => {
                        e.preventDefault();
                        navigate(`/argo-workflows/${wf.namespace}/${wf.name}`);
                      }}
                      style={{ flex: 1 }}
                    >
                      {wf.name}
                    </a>
                    <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>
                      {formatDuration(wf.startedAt, wf.finishedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
